import React, {FC} from 'react';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';
import memoize from 'memoizee';

// @ts-ignore
import connectToStores from 'alt-utils/lib/connectToStores';
import mapValues from 'lodash/mapValues';

import {ExportedStore, StoreState} from '@store/@types/alt/Store';

// This variable store all selectors caches for better performance experience
const cache: any = {};
const getCashIfNewValueIsEqualWithCache = (
  selectorName: string,
  selectorFunc,
  selectors,
  stores
) => {
  const funcArgs = {};
  const args = selectors.map((el, index) => {
    const r = el.executor(stores);
    if (typeof r === 'function') {
      funcArgs[index] = cache[el.selectorName]?.filteredArgs;
    }
    return r;
  });
  const res = selectorFunc(...args);
  if (typeof res === 'function') {
    const filteredArgs = args.map((el, index) => (typeof el === 'function' ? funcArgs[index] : el));
    if (!isEqual(filteredArgs, cache[selectorName]?.filteredArgs)) {
      cache[selectorName] = {
        filteredArgs,
        result: memoize(res),
      };
    }

    return cache[selectorName]?.result;
  }

  if (!isEqual(cache[selectorName], res)) {
    cache[selectorName] = res;
  }

  return cache[selectorName];
};

const getCashIfResIsMatch = (selectorName: string, res: any) => {
  if (!isEqual(cache[selectorName], res)) {
    cache[selectorName] = res;
  }

  return cache[selectorName];
};

type SelectorStores<T> = {[key in string]: ExportedStore<T>};
interface Selector<T = unknown> {
  selectorName: string;
  stores: SelectorStores<T>;
  executor: (stores: SelectorStores<T>) => any;
  (): any;
}

// Infer Props given a component
type ComponentProps<Component> = Component extends React.FC<infer Props> ? Props : never;

// Given an object of selectors, return an object of with the same keys
// mapped to the selectors contents
type SelectorLift<Selectors extends {[key in string]: Selector<any>}> = {
  [Prop in keyof Selectors]: Selectors[Prop] extends Selector<infer data> ? data : never;
};

// Compare the selectors with the component's props to ensure their types match
type ValidStateProps<State extends {[key in string]: Selector<any>}, Component> =
  ComponentProps<Component> extends SelectorLift<State> ? State : never;

type ValidDispatch<Dispatch extends {[key in string]: (...args: any) => any}, Component> =
  ComponentProps<Component> extends Dispatch ? Dispatch : never;

export function connect<
  Component extends FC<any>,
  StateProps extends {[key in string]: Selector<any>}
>(
  WrappedComponent: Component,
  mapStateToProps: ValidStateProps<StateProps, Component>
): React.FC<Omit<ComponentProps<Component>, keyof StateProps>>;

export function connect<
  Component extends FC<any>,
  StateProps extends {[key in string]: Selector<any>} = {},
  DispatchProps extends {[key in string]: (...args: any) => any} = {}
>(
  WrappedComponent: Component,
  mapStateToProps: ValidStateProps<StateProps, Component> | undefined,
  mapDispatchToProps: ValidDispatch<DispatchProps, Component>
): React.FC<Omit<ComponentProps<Component>, keyof StateProps | keyof DispatchProps>>;

export function connect<
  Component extends FC<any>,
  StateProps extends {[key in string]: Selector<any>},
  DispatchProps extends {[key in string]: (...args: any) => any}
>(
  WrappedComponent: Component,
  mapStateToProps?: ValidStateProps<StateProps, Component>,
  mapDispatchToProps?: ValidDispatch<DispatchProps, Component>
): React.FC<Omit<ComponentProps<Component>, keyof StateProps | keyof DispatchProps>> {
  // We have to return React.memo to fix performance issues
  return React.memo(
    connectToStores(
      class extends React.Component<any, {}> {
        static getStores(): Array<ExportedStore<unknown>> {
          if (mapStateToProps) {
            return Array.from(
              new Set(
                Object.values(mapStateToProps).reduce((res, cur) => {
                  res = res.concat(Object.values(cur.stores) as any);
                  return res;
                }, [])
              )
            );
          }

          return [];
        }

        static getPropsFromStores() {
          let state = {};

          if (mapStateToProps) {
            state = mapValues(mapStateToProps, (el: any) => {
              return el.executor(el.stores);
            });
          }

          return state;
        }

        public componentWillUnmount() {
          if (mapStateToProps) {
            mapValues(mapStateToProps, (el: any) => {
              delete cache[el.selectorName];
            });
          }
        }

        render() {
          const ConnectedComponent: any = WrappedComponent;
          let dispatchProps = {};
          if (mapDispatchToProps) {
            dispatchProps = mapDispatchToProps;
          }
          return <ConnectedComponent {...this.props} {...dispatchProps} />;
        }
      }
    )
  );
}

export function createSelector<T>(store: ExportedStore<T>): Selector<T>;

export function createSelector<T, V>(
  selectors: [Selector<T>],
  func: (state: StoreState<T>) => V
): Selector<V>;

// Copy this overload and add more elements to the tuple to use more selectors at once.
// Because of how typescript works we can't infer the tuples needed here I dont think.
export function createSelector<T, TT, V>(
  selectors: [Selector<T>, Selector<TT>],
  func: (storeA: StoreState<T>, storeB: StoreState<TT>) => V
): Selector<V>;

export function createSelector<T, TT, TTT, V>(
  selectors: [Selector<T>, Selector<TT>, Selector<TTT>],
  func: (storeA: StoreState<T>, storeB: StoreState<TT>, storeC: StoreState<TTT>) => V
): Selector<V>;

export function createSelector<S1, S2, S3, S4, V>(
  selectors: [Selector<S1>, Selector<S2>, Selector<S3>, Selector<S4>],
  func: (
    storeA: StoreState<S1>,
    storeB: StoreState<S2>,
    storeC: StoreState<S3>,
    storeD: StoreState<S4>
  ) => V
): Selector<V>;

export function createSelector<S1, S2, S3, S4, S5, V>(
  selectors: [Selector<S1>, Selector<S2>, Selector<S3>, Selector<S4>, Selector<S5>],
  func: (
    storeA: StoreState<S1>,
    storeB: StoreState<S2>,
    storeC: StoreState<S3>,
    storeD: StoreState<S4>,
    storeE: StoreState<S5>
  ) => V
): Selector<V>;

export function createSelector<S1, S2, S3, S4, S5, S6, V>(
  selectors: [Selector<S1>, Selector<S2>, Selector<S3>, Selector<S4>, Selector<S5>, Selector<S6>],
  func: (
    storeA: StoreState<S1>,
    storeB: StoreState<S2>,
    storeC: StoreState<S3>,
    storeD: StoreState<S4>,
    storeE: StoreState<S5>,
    storeF: StoreState<S6>
  ) => V
): Selector<V>;

export function createSelector<S1, S2, S3, S4, S5, S6, S7, V>(
  selectors: [
    Selector<S1>,
    Selector<S2>,
    Selector<S3>,
    Selector<S4>,
    Selector<S5>,
    Selector<S6>,
    Selector<S7>
  ],
  func: (
    storeA: StoreState<S1>,
    storeB: StoreState<S2>,
    storeC: StoreState<S3>,
    storeD: StoreState<S4>,
    storeE: StoreState<S5>,
    storeF: StoreState<S6>,
    storeG: StoreState<S7>
  ) => V
): Selector<V>;

export function createSelector<S1, S2, S3, S4, S5, S6, S7, S8, V>(
  selectors: [
    Selector<S1>,
    Selector<S2>,
    Selector<S3>,
    Selector<S4>,
    Selector<S5>,
    Selector<S6>,
    Selector<S7>,
    Selector<S8>
  ],
  func: (
    storeA: StoreState<S1>,
    storeB: StoreState<S2>,
    storeC: StoreState<S3>,
    storeD: StoreState<S4>,
    storeE: StoreState<S5>,
    storeF: StoreState<S6>,
    storeG: StoreState<S7>,
    storeH: StoreState<S8>
  ) => V
): Selector<V>;

export function createSelector<S1, S2, S3, S4, S5, S6, S7, S8, S9, V>(
  selectors: [
    Selector<S1>,
    Selector<S2>,
    Selector<S3>,
    Selector<S4>,
    Selector<S5>,
    Selector<S6>,
    Selector<S7>,
    Selector<S8>,
    Selector<S9>
  ],
  func: (
    storeA: StoreState<S1>,
    storeB: StoreState<S2>,
    storeC: StoreState<S3>,
    storeD: StoreState<S4>,
    storeE: StoreState<S5>,
    storeF: StoreState<S6>,
    storeG: StoreState<S7>,
    storeH: StoreState<S8>,
    storeI: StoreState<S9>
  ) => V
): Selector<V>;

export function createSelector<T>(
  selectors: Selector<T>[] | ExportedStore<T>,
  func?: (...state: T[]) => any
): Selector<T> {
  // each selector should has uniq selector name
  const selectorName = uniqueId('selector') as string;

  if (isSelectorWithFunc(selectors, func)) {
    if (!func) throw new Error('No function given to createSelector');

    const stores: SelectorStores<T> = selectors.reduce((res, cur) => ({...res, ...cur.stores}), {});
    const resFn = (localStores?) =>
      getCashIfNewValueIsEqualWithCache(selectorName, func, selectors, localStores || stores);
    resFn.stores = stores;
    resFn.executor = resFn;
    resFn.selectorName = selectorName;
    return resFn;
  }

  const CurrentStore: ExportedStore<T> = selectors;
  const StoreName = CurrentStore.displayName;
  const stores = {[StoreName]: CurrentStore};
  const resFn = (localStores?: Record<string, ExportedStore<any>>) =>
    getCashIfResIsMatch(selectorName, (localStores || stores)[StoreName].getState());
  resFn.stores = stores;
  resFn.executor = resFn;
  resFn.selectorName = selectorName;

  return resFn;
}

function isSelectorWithFunc<T>(
  selectors: Selector<T>[] | ExportedStore<T>,
  func
): selectors is Selector<T>[] {
  return !!func;
}

export function mockSelector<T>(data: T) {
  const resFn = (stores?) => data;
  resFn.stores = {};
  resFn.executor = resFn;
  return resFn;
}
